'use client';

import { useLocalStorage } from '@shared/hooks';

const MAX_RECENT_SEARCHES = 5;

type RecentSearchesMap = {
  [vertical: string]: string[];
};

function isRecentSearchesMap(value: unknown): value is RecentSearchesMap {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every(entry => Array.isArray(entry) && entry.every((item): item is string => typeof item === 'string'));
}

/**
 * Hook for managing recent searches in localStorage, categorized by a search type.
 *
 * @param vertical The vertical for the searches (e.g., 'home', 'lotteries', 'casino', 'live-casino', 'scratchcards').
 */
function useRecentSearches(vertical: string) {
  const [recentSearches, setRecentSearches] = useLocalStorage<RecentSearchesMap>('ll-recent-searches', {}, isRecentSearchesMap);

  /**
   * Adds a new search term to the recent searches list for the current vertical.
   * If the term already exists, it's moved to the top of the list.
   * The list is capped at a maximum of 5 items per search type.
   *
   * @param searchTerm The search term to add.
   */
  const addRecentSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) return;

    const updatedSearches = { ...recentSearches };
    const searchesForType = updatedSearches[vertical] || [];
    const filteredSearches = searchesForType.filter(search => search.toLowerCase() !== searchTerm.toLowerCase());
    const newSearches = [searchTerm, ...filteredSearches];

    updatedSearches[vertical] = newSearches.slice(0, MAX_RECENT_SEARCHES);

    setRecentSearches(updatedSearches);
  };

  const searches = recentSearches[vertical] || [];

  return { searches, addRecentSearch };
}

export { useRecentSearches };
